/**
 * Google Jobs API Service - Using Backend Proxy
 * This version connects to your Vercel backend instead of SerpApi directly
 */

// ⚠️ استبدل هذا بـ URL الخاص بك بعد النشر على Vercel
const BACKEND_URL = 'https://serpapi-proxy-1m22o2vvc-salahs-projects-31d9db4f.vercel.app'; // ← غيّر هذا!

const DEFAULT_TIMEOUT = 60000;
const MAX_RETRIES = 3;

/**
 * Check if backend URL is configured
 */
export function isGoogleJobsAPIConfigured() {
  const configured = BACKEND_URL && !BACKEND_URL.includes('your-backend-url');
  console.log('🔑 Backend Proxy configured:', configured);
  if (!configured) {
    console.warn('⚠️ Please update BACKEND_URL in googleJobsAPI.js');
    console.warn('   Deploy backend-proxy to Vercel first');
  }
  return configured;
}

/**
 * Retry helper
 */
async function retryRequest(fn, retries = MAX_RETRIES, delay = 2000) {
  for (let i = 0; i < retries; i++) {
    try {
      if (i > 0) {
        console.log(`🔄 Retry attempt ${i}/${retries - 1}...`);
      }
      return await fn();
    } catch (error) {
      console.error(`❌ Attempt ${i + 1} failed:`, error.message);
      
      if (i === retries - 1) {
        console.error('🚫 All retry attempts failed');
        throw error;
      }
      
      console.log(`⏳ Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 2;
    }
  }
}

/**
 * Search for jobs using Backend Proxy (single page)
 */
export async function searchGoogleJobs({
  query = 'jobs',
  location = 'United Kingdom',
  datePosted = 'week',
  remoteOnly = false,
  page = 1,
  timeout = DEFAULT_TIMEOUT,
}) {
  return retryRequest(async () => {
    try {
      if (!isGoogleJobsAPIConfigured()) {
        console.warn('⚠️ Backend URL not configured - skipping');
        return {
          success: false,
          error: 'Backend URL not configured. Please deploy backend-proxy first.',
          jobs: [],
          total: 0,
        };
      }

      let searchQuery = query;
      if (remoteOnly) {
        searchQuery += ' remote';
      }

      console.log('🔍 Searching Google Jobs via Backend Proxy for:', searchQuery);

      // Build request URL
      const params = new URLSearchParams({
        query: searchQuery,
        location: location,
        datePosted: datePosted,
        page: page.toString(),
      });

      const url = `${BACKEND_URL}/api/jobs?${params.toString()}`;
      
      console.log('📡 Request URL:', url);
      console.log(`⏱️ Timeout: ${timeout}ms`);

      // Create timeout controller
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.error(`⏱️ Request timeout after ${timeout}ms`);
      }, timeout);

      console.log('📤 Sending request to Backend Proxy...');

      // Make request to backend proxy
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log('📥 Response received - Status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Unknown error');
      }

      const data = result.data;

      // Check if we have jobs data
if (!data.jobs_results || !Array.isArray(data.jobs_results) || data.jobs_results.length === 0) {
  console.warn('⚠️ No jobs found in response');
  return {
    success: true,
    jobs: [],
    total: 0,
    page: page,
    message: 'لم يتم العثور على وظائف. جرّب كلمات بحث أو موقع مختلف.',
    userMessage: 'No jobs found. Try different keywords or location.',
  };
}


      console.log('🔄 Transforming', data.jobs_results.length, 'jobs...');
      const jobs = transformGoogleJobs(data.jobs_results);

      console.log('✅ Successfully fetched', jobs.length, 'jobs from Google Jobs');

      return {
        success: true,
        jobs: jobs,
        total: jobs.length,
        page: page,
        filters: data.filters || [],
        searchMetadata: data.search_metadata || {},
        raw: data,
      };

    } catch (error) {
      console.error('❌ Backend Proxy Error:', error);
      
      let errorMessage = error.message;
      
      if (error.name === 'AbortError') {
        errorMessage = `Request timeout after ${timeout / 1000} seconds. Please check your internet connection.`;
      } else if (error.message.includes('Network request failed')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Could not connect to backend. Please check if backend is deployed.';
      }

      console.error('💬 User-friendly error:', errorMessage);

      return {
        success: false,
        error: errorMessage,
        jobs: [],
        total: 0,
        technicalError: error.message,
      };
    }
  }, MAX_RETRIES, 2000);
}

/**
 * Transform Google Jobs API response to app format
 */
function transformGoogleJobs(apiJobs) {
  if (!Array.isArray(apiJobs)) {
    console.warn('⚠️ Invalid jobs data');
    return [];
  }

  console.log('🔄 Transforming', apiJobs.length, 'jobs...');

  const transformed = apiJobs.map((job, index) => {
    try {
      const salaryInfo = extractSalaryFromGoogle(job);
      const matchScore = calculateMatchScore(job);
      const location = job.location || 'Remote';
      const company = job.company_name || 'Company';

      return {
        id: job.job_id || `google_${Date.now()}_${index}`,
        jobId: job.job_id || `google_${Date.now()}_${index}`,
        title: job.title || 'Untitled Position',
        client: company,
        company: company,
        clientLogo: job.thumbnail || generateLogoPlaceholder(company),
        logo: job.thumbnail,
        location: location,
        budget: salaryInfo.budget,
        budgetRange: salaryInfo.budgetRange,
        salary: salaryInfo.budget,
        type: extractJobType(job),
        jobType: extractJobType(job),
        projectType: extractProjectType(job),
        duration: 'Flexible',
        remote: isRemoteJob(job),
        isRemote: isRemoteJob(job),
        posted: formatPostedDate(job.detected_extensions?.posted_at),
        description: job.description || 'No description available',
        requirements: extractRequirements(job),
        skills: extractSkills(job),
        requiredSkills: extractSkills(job),
        deliverables: ['Project Completion', 'Documentation', 'Quality Work'],
        matchScore: matchScore,
        featured: job.extensions?.includes('Featured') || false,
        urgent: isUrgent(job),
        
        clientInfo: {
          rating: 4.5,
          reviewsCount: 0,
          projectsPosted: 0,
          hireRate: 'N/A',
          location: location,
          memberSince: 'N/A',
        },
        
        paymentTerms: {
          type: salaryInfo.paymentType,
          milestones: false,
          escrow: false,
          paymentVerified: true,
        },
        
        apiSource: 'GoogleJobs',
        dataSource: 'API',
        applyLink: job.apply_options?.[0]?.link || job.share_link,
        jobPublisher: job.via || 'Google Jobs',
        relatedLinks: job.related_links || [],
        applyOptions: job.apply_options || [],
        extensions: job.extensions || [],
        detectedExtensions: job.detected_extensions || {},
        
        _originalData: job,
      };
    } catch (error) {
      console.error('❌ Error transforming job at index', index, ':', error);
      return null;
    }
  }).filter(job => job !== null);

  console.log('✅ Successfully transformed', transformed.length, 'jobs');
  
  return transformed;
}

// Helper functions (same as before)
function extractSalaryFromGoogle(job) {
  let budget = 'Competitive';
  let budgetRange = { min: 0, max: 0 };
  let paymentType = 'Fixed Price';

  if (job.detected_extensions?.salary) {
    budget = job.detected_extensions.salary;
    
    const salaryMatch = budget.match(/[\£\$]?([\d,]+)(?:\s*-\s*[\£\$]?([\d,]+))?/);
    if (salaryMatch) {
      const min = parseInt(salaryMatch[1].replace(/,/g, ''));
      const max = salaryMatch[2] ? parseInt(salaryMatch[2].replace(/,/g, '')) : min;
      budgetRange = { min, max };
    }
    
    if (budget.includes('hour')) {
      paymentType = 'Hourly Rate';
    } else if (budget.includes('year') || budget.includes('annum')) {
      paymentType = 'Annual Salary';
    } else if (budget.includes('month')) {
      paymentType = 'Monthly Rate';
    }
  }

  return { budget, budgetRange, paymentType };
}

function extractJobType(job) {
  const extensions = job.extensions || [];
  if (extensions.some(ext => ext.toLowerCase().includes('contract'))) return 'Contract';
  if (extensions.some(ext => ext.toLowerCase().includes('part'))) return 'Part-time';
  if (extensions.some(ext => ext.toLowerCase().includes('full'))) return 'Full-time';
  if (extensions.some(ext => ext.toLowerCase().includes('intern'))) return 'Internship';
  return 'Full-time';
}

function extractProjectType(job) {
  const extensions = job.extensions || [];
  if (extensions.some(ext => ext.toLowerCase().includes('contract'))) return 'Contract';
  if (extensions.some(ext => ext.toLowerCase().includes('freelance'))) return 'Freelance';
  if (extensions.some(ext => ext.toLowerCase().includes('part'))) return 'Part-time';
  if (extensions.some(ext => ext.toLowerCase().includes('full'))) return 'Full-time';
  if (extensions.some(ext => ext.toLowerCase().includes('intern'))) return 'Internship';
  return 'Full-time';
}

function isRemoteJob(job) {
  const location = (job.location || '').toLowerCase();
  const extensions = (job.extensions || []).join(' ').toLowerCase();
  const title = (job.title || '').toLowerCase();
  return location.includes('remote') || extensions.includes('remote') || title.includes('remote');
}

function formatPostedDate(postedAt) {
  return postedAt || 'Recently';
}

function extractRequirements(job) {
  const requirements = [];
  const description = job.description || '';
  
  if (description.toLowerCase().includes('experience')) {
    requirements.push('Relevant experience required');
  }
  if (description.toLowerCase().includes('degree')) {
    requirements.push('Degree preferred');
  }
  
  if (requirements.length === 0) {
    requirements.push('Strong communication skills', 'Self-motivated', 'Attention to detail');
  }
  
  return requirements.slice(0, 5);
}

function extractSkills(job) {
  const skills = [];
  const text = `${job.title || ''} ${job.description || ''}`.toLowerCase();
  const techSkills = ['react', 'node', 'python', 'java', 'javascript', 'typescript', 'aws', 'docker', 'sql'];
  
  techSkills.forEach(skill => {
    if (text.includes(skill)) {
      skills.push(skill.charAt(0).toUpperCase() + skill.slice(1));
    }
  });
  
  if (skills.length === 0) {
    skills.push('Communication', 'Problem Solving', 'Teamwork');
  }
  
  return [...new Set(skills)].slice(0, 6);
}

function isUrgent(job) {
  const postedAt = job.detected_extensions?.posted_at || '';
  return postedAt.includes('today') || postedAt.includes('1 day') || postedAt.includes('yesterday');
}

function calculateMatchScore(job) {
  let score = 60;
  if (isRemoteJob(job)) score += 15;
  
  const postedAt = job.detected_extensions?.posted_at || '';
  if (postedAt.includes('today') || postedAt.includes('1 day')) {
    score += 15;
  }
  
  if (job.detected_extensions?.salary) score += 10;
  if (job.extensions?.includes('Featured')) score += 5;
  score += Math.floor(Math.random() * 10);
  
  return Math.min(score, 99);
}

function generateLogoPlaceholder(companyName) {
  if (!companyName) return null;
  const domain = companyName.toLowerCase().replace(/\s+/g, '') + '.com';
  return `https://logo.clearbit.com/${domain}`;
}

export async function searchFreelanceJobsGoogle(params = {}) {
  return searchGoogleJobs({
    query: 'developer designer writer jobs',
    location: 'United Kingdom',
    datePosted: 'week',
    timeout: 60000,
    ...params,
  });
}

export async function getGoogleJobDetails(jobId) {
  console.warn('⚠️ Google Jobs API does not support job details endpoint');
  return {
    success: false,
    error: 'Job details endpoint not available',
  };
}


// ----------------------------------------------------------------------
// 🚀 NEW FUNCTION TO FETCH ALL PAGES 🚀
// ----------------------------------------------------------------------

/**
 * 🚀 Fetch ALL available jobs by looping through pages
 * NOTE: This will make multiple API calls and can be slow/costly.
 * It also assumes the backend API supports the 'page' parameter.
 */
export async function fetchAllGoogleJobs(
  {
    query = 'jobs',
    location = 'United Kingdom',
    datePosted = 'week',
    remoteOnly = false,
    timeout = 60000,
    maxPages = 5, // ⚠️ Add a safeguard to prevent excessive calls
  } = {}
) {
  let allJobs = [];
  let currentPage = 1;
  let hasMorePages = true;

  console.log('🔄 Starting full job fetch (page 1 to maxPage:', maxPages, ')');

  while (hasMorePages && currentPage <= maxPages) {
    console.log(`\n➡️ Fetching page ${currentPage}...`);
    
    // Call the existing search function with the current page number
    const result = await searchGoogleJobs({
      query,
      location,
      datePosted,
      remoteOnly,
      page: currentPage,
      timeout,
    });

    if (result.success && result.jobs && result.jobs.length > 0) {
      console.log(`✅ Fetched ${result.jobs.length} jobs on page ${currentPage}`);
      allJobs = allJobs.concat(result.jobs);
      
      // Determine if there might be more pages
      currentPage++;
    } else if (result.success && result.jobs.length === 0) {
      console.log('🛑 No jobs found on this page. Stopping fetch.');
      hasMorePages = false; // Stop the loop if a page returns no jobs
    } else {
      console.error('❌ Error fetching page', currentPage, ':', result.error || 'Unknown error');
      // Stop on error
      hasMorePages = false;
    }
  }

  console.log(`\n🎉 Full fetch complete. Total jobs collected: ${allJobs.length}`);
  
  return {
    success: true,
    jobs: allJobs,
    total: allJobs.length,
    message: `Successfully collected ${allJobs.length} jobs across ${currentPage - 1} pages.`,
  };
}

// ----------------------------------------------------------------------

// Alias for compatibility with other API services
export const isAPIConfigured = isGoogleJobsAPIConfigured;

export default {
  searchGoogleJobs,
  searchFreelanceJobsGoogle,
  getGoogleJobDetails,
  isGoogleJobsAPIConfigured,
  isAPIConfigured,
  // 🚀 New function added to exports
  fetchAllGoogleJobs, 
};